/**
 * The files an agent created or changed in a session (owner, 08/10/2026: "se você gerar algum arquivo, coloca o
 * NOME e o CAMINHO local onde você gerou"): what git reported, and what the agent wrote or edited through its tools
 * (the timeline's activity lines: "Write: src/a.ts"). Each with its name, its path in the project, and — on the
 * desktop, where the project's folder is known — the full path on this computer.
 */

import type { TimelineEntry } from '../lib/types'

/** The tools that write a file: Claude Code's, Codex's, and the usual names. */
const WRITERS = new Set(['write', 'edit', 'multiedit', 'notebookedit', 'create', 'apply_patch', 'str_replace_editor', 'write_file', 'edit_file'])

export interface TouchedFile {
  /** The file's name: what a person looks for first. */
  name: string
  /** As reported: relative to the project, or already absolute. */
  path: string
  /** The full path on this computer, when known (the desktop with the project's folder). */
  local: string | null
  /** The agent wrote it with a tool in this session (not only git's count). */
  byAgent: boolean
}

const isAbsolute = (path: string) => /^([a-zA-Z]:[\\/]|\/|\\\\)/.test(path)

function join(folder: string, path: string): string {
  const windows = folder.includes('\\')
  const sep = windows ? '\\' : '/'
  const clean = path.replace(/^\.?[\\/]+/, '').replace(/[\\/]/g, sep)
  return folder.replace(/[\\/]+$/, '') + sep + clean
}

export function filesTouched(paths: string[], timeline: TimelineEntry[], folder: string | null): TouchedFile[] {
  const byKey = new Map<string, TouchedFile>()
  const add = (path: string, byAgent: boolean) => {
    const trimmed = path.trim()
    if (!trimmed) return
    const key = trimmed.replace(/\\/g, '/').toLowerCase()
    const existing = byKey.get(key)
    if (existing) {
      existing.byAgent = existing.byAgent || byAgent
      return
    }
    const name = trimmed.split(/[\\/]/).filter(Boolean).pop() ?? trimmed
    const local = isAbsolute(trimmed) ? trimmed : folder ? join(folder, trimmed) : null
    byKey.set(key, { name, path: trimmed, local, byAgent })
  }
  for (const entry of timeline) {
    if (entry.eventType !== 'agent.activity') continue
    const tool = String(entry.payload?.tool ?? '').toLowerCase()
    const target = entry.payload?.target
    if (WRITERS.has(tool) && typeof target === 'string' && target.length < 400 && !/\s{2,}|[\n\r]/.test(target)) add(target, true)
  }
  for (const path of paths) add(path, false)
  return [...byKey.values()]
}
